/**
 * rng — 确定性伪随机基元（Bones 骨架层）
 * FNV-1a 32-bit 哈希 + Mulberry32 PRNG；相同输入 → 相同序列。
 */

/** FNV-1a 32-bit hash */
export function fnv1a(str: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

/** Mulberry32 PRNG，返回 [0,1) 均匀分布 */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

export type Rng = () => number;

/** 闭区间 [min, max] 整数 */
export function randInt(rng: Rng, min: number, max: number): number {
  return min + Math.floor(rng() * (max - min + 1));
}

/** 从数组中等概率取一项 */
export function pick<T>(rng: Rng, arr: readonly T[]): T {
  return arr[Math.floor(rng() * arr.length)];
}

/** 独立概率判定 */
export function chance(rng: Rng, p: number): boolean {
  return rng() < p;
}

export interface WeightedItem<T> {
  item: T;
  weight: number;
}

/** 按权重取一项（不消耗额外随机流之外的状态） */
export function pickWeighted<T>(rng: Rng, items: readonly WeightedItem<T>[]): T {
  const total = items.reduce((s, it) => s + it.weight, 0);
  let roll = rng() * total;
  for (const it of items) {
    roll -= it.weight;
    if (roll < 0) return it.item;
  }
  return items[items.length - 1].item;
}

/** 按权重不放回地取 count 个互异项 */
export function pickWeightedDistinct<T>(
  rng: Rng,
  items: readonly WeightedItem<T>[],
  count: number,
): T[] {
  const pool = [...items];
  const out: T[] = [];
  while (out.length < count && pool.length > 0) {
    const chosen = pickWeighted(rng, pool);
    out.push(chosen);
    const idx = pool.findIndex((it) => it.item === chosen);
    if (idx >= 0) pool.splice(idx, 1);
  }
  return out;
}
